import type { DraftDocumentModel, DraftSection, ProseMirrorDoc, ProseMirrorNode } from "@/types/drafting";

export function extractPlainText(node: ProseMirrorNode | undefined): string {
  if (!node) return "";
  if (typeof node.text === "string") return node.text;
  if (!Array.isArray(node.content)) return "";
  return node.content.map((child) => extractPlainText(child)).join("");
}

export function extractSectionBodyText(section: DraftSection): string {
  const blocks = section.content?.content ?? [];
  return blocks
    .map((block) => extractPlainText(block).trim())
    .filter(Boolean)
    .join("\n\n");
}

export function stripSectionNumberPrefix(title: string): string {
  return title.replace(/^\d+(\.\d+)*\.?\s+/, "");
}

export function createEmptyDraftDocument(title = "Untitled Draft"): DraftDocumentModel {
  return {
    type: "draft-document",
    title,
    sections: [],
  };
}

export function toProseMirrorDoc(model: DraftDocumentModel): ProseMirrorDoc {
  const content: ProseMirrorNode[] = [];

  for (const section of model.sections) {
    const level = section.title.match(/^\d+(\.\d+)*\s/) ? 2 : 1;
    content.push({
      type: "heading",
      attrs: { level, sectionId: section.sectionId, sectionKey: section.sectionKey },
      content: [{ type: "text", text: section.title }],
    });

    if (Array.isArray(section.content.content)) {
      content.push(...section.content.content);
    }
  }

  return { type: "doc", content };
}

export function fromProseMirrorDoc(doc: ProseMirrorDoc, fallback: DraftDocumentModel): DraftDocumentModel {
  const sections: DraftSection[] = [];
  let current: DraftSection | null = null;
  const bodyNodes: ProseMirrorNode[] = [];

  const flush = () => {
    if (!current) return;
    sections.push({
      ...current,
      content: { type: "doc", content: bodyNodes.length ? [...bodyNodes] : [] },
    });
    bodyNodes.length = 0;
    current = null;
  };

  for (const node of doc.content ?? []) {
    if (node.type === "heading") {
      flush();
      const title = extractPlainText(node);
      const attrs = node.attrs ?? {};
      const sectionKey =
        typeof attrs.sectionKey === "string"
          ? attrs.sectionKey
          : title.toLowerCase().replace(/\s+/g, "-").slice(0, 64);
      const sectionId =
        typeof attrs.sectionId === "string" ? attrs.sectionId : `sec-${sectionKey}`;
      const existing =
        fallback.sections.find((s) => s.sectionKey === sectionKey) ??
        fallback.sections.find((s) => s.sectionId === sectionId);
      current = {
        sectionId: existing?.sectionId ?? sectionId,
        sectionKey: existing?.sectionKey ?? sectionKey,
        title: title || existing?.title || sectionKey,
        content: { type: "doc", content: [] },
      };
      continue;
    }

    if (current) {
      bodyNodes.push(node);
    }
  }

  flush();

  if (!sections.length) {
    return fallback;
  }

  return mergeParsedSectionsIntoModel(fallback, sections);
}

export function mergeParsedSectionsIntoModel(
  base: DraftDocumentModel,
  parsed: DraftSection[]
): DraftDocumentModel {
  if (!parsed.length) {
    return base;
  }

  const sections = base.sections.map((section) => {
    const updated =
      parsed.find((item) => item.sectionKey === section.sectionKey) ??
      parsed.find((item) => item.sectionId === section.sectionId);
    if (!updated) {
      return section;
    }
    return {
      ...section,
      title: updated.title || section.title,
      content: updated.content,
    };
  });

  return { ...base, sections };
}

export function draftDocumentHasContent(model: DraftDocumentModel): boolean {
  return model.sections.some((section) => extractSectionBodyText(section).trim().length > 0);
}

/**
 * Prefer incoming draft JSON when it has section body text; otherwise keep lastValid.
 */
export function resolveEditorDocument(incoming: unknown, lastValid: unknown): unknown {
  const incomingModel = parseDraftDocumentModel(incoming);
  if (draftDocumentHasContent(incomingModel)) return incoming;
  const lastModel = parseDraftDocumentModel(lastValid);
  if (draftDocumentHasContent(lastModel)) return lastValid;
  return incoming;
}

export function parseDraftDocumentModel(input: unknown): DraftDocumentModel {
  if (!input || typeof input !== "object") {
    return createEmptyDraftDocument();
  }
  const record = input as Record<string, unknown>;
  if (record.type === "draft-document" && Array.isArray(record.sections)) {
    return input as DraftDocumentModel;
  }
  if (record.type === "doc" && Array.isArray(record.content)) {
    const base = createEmptyDraftDocument();
    return fromProseMirrorDoc(input as ProseMirrorDoc, base);
  }
  return createEmptyDraftDocument();
}

export function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) resolve(e.target.result as string);
      else reject(new Error("Failed to read file"));
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}

export async function encodeSourceDocuments(
  docs: Array<{ file?: File; docType: string; fileName: string }>
) {
  const withFiles = docs.filter((doc): doc is { file: File; docType: string; fileName: string } =>
    Boolean(doc.file)
  );
  if (!withFiles.length) return [];
  return Promise.all(
    withFiles.map(async (doc) => ({
      fileName: doc.fileName ?? doc.file.name,
      docType: doc.docType,
      fileBase64: await readFileAsBase64(doc.file),
    }))
  );
}
